// Endpoint definitions injected in incomingShipmentsApi.js; DTO → VM runs in transformResponse.
import { TAG_TYPES, linkedContractsTag } from '@/lib/api/tags';
import { processServerError, handledError } from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import { buildContractFiltersInput, buildShipmentFiltersInput } from '@/lib/mappers';
import {
  buildIncomingShipmentsQuery,
  processIncomingShipmentsPage,
  processShipmentFilterOptions,
  processIncomingShipmentDetail,
  processLinkedContracts,
  processLinkedDeclarations,
  buildIncomingShipmentFormData,
  processIncomingShipmentForm,
  buildIncomingShipmentUpdateFormData,
  isIncomingShipmentLocked,
  processCreatedIncomingShipment,
} from '../mappers/incomingShipmentsMappers';

// Batch-group writes live under the active module segment; the list is GraphQL.
const PATHS = {
  create: (module) => `/${module}/batch-group`,
  detail: (module, id) => `/${module}/batch-group/${id}`,
  unlinkContract: (module, id, contractId) =>
    `/${module}/contracts/batch-group/${id}/unlink/${contractId}`,
};

// The fields the Incoming Shipments table renders; `BatchGroup` has 60+.
export const INCOMING_SHIPMENTS_QUERY = `
  query Shipments($limit: Int, $page: Int, $search: ShipmentSearch, $orderBy: [ShipmentOrderBy!], $filters: ShipmentFilters) {
    shipments(limit: $limit, page: $page, search: $search, orderBy: $orderBy, filters: $filters) {
      nodes {
        id
        shipmentId
        material { name }
        sentBySupplier { companyname }
        receivedAtSite { siteName }
        terminal
        vessel
        status
        totalContractsLinked
        totalDeclarationsReceived
        totalDeclarationsLinked
        workingUnit
        declarationUnit
        trackingUnit
        shipmentQuantityWorking
        shipmentQuantityDeclaration
        shipmentQuantityTracking
        receivedDeclarationQuantityWorking
        receivedDeclarationQuantityDeclaration
        receivedDeclarationQuantityTracking
        pendingShipmentQuantityWorking
        pendingShipmentQuantityDeclaration
        pendingShipmentQuantityTracking
        availableShipmentQuantityWorking
        availableShipmentQuantityDeclaration
        availableShipmentQuantityTracking
        unassignedContractQuantityWorking
        unassignedContractQuantityDeclaration
        unassignedContractQuantityTracking
        ghgEmissions
        ghgEmissionSavings
        rawMaterialType
        countryOfOrigin
        feedstockCategory
        preferredAllocation
        dateOfDischarge
        dateOfDispatch
        daysLeft
        discrepancyCount
        discrepancies { type message attribute refKind contractId declarationId daysLeft }
      }
      pageInfo {
        total
        page
        limit
      }
    }
  }
`;

// The filter panel's Material and Supplier choices — only what the table's shipments carry.
export const SHIPMENT_FILTER_OPTIONS_QUERY = `
  query ShipmentFilterOptions($filters: ShipmentFilters) {
    shipmentFilterOptions(filters: $filters) {
      materials { id name }
      suppliers { id name }
    }
  }
`;

// Shipment details ▸ Linked Contracts tab. `limit: 100` is the server's max, so one page holds all.
export const LINKED_CONTRACTS_FOR_SHIPMENT_QUERY = `
  query LinkedContractsForShipment($shipmentId: ID!, $filters: ContractFilters) {
    linkedContractsForShipment(shipmentId: $shipmentId, limit: 100, page: 1, filters: $filters) {
      nodes {
        linkId
        assignedQuantityWorking
        assignedQuantityDeclaration
        linkedShipmentIds
        contract {
          id
          contractId
          status
          terminal
          workingUnit
          declarationUnit
          workingQuantity
          declarationQuantity
          shippedWorkingQuantity
          shippedDeclarationQuantity
          outstandingWorkingQuantity
          outstandingDeclarationQuantity
          ghgEmissions
          ghgEmissionSavings
          rawMaterialType
          countryOfOrigin
          feedstockCategory
          preferredAllocation
          deliverBy
          discrepancyCount
          discrepancies { items { type message attribute contractValue shipmentValue } }
        }
      }
    }
  }
`;

// Shipment details ▸ Linked Declarations tab. `limit: 100` is the server's max, so one page holds all.
export const LINKED_DECLARATIONS_FOR_SHIPMENT_QUERY = `
  query LinkedDeclarationsForShipment($shipmentId: ID!) {
    linkedDeclarationsForShipment(shipmentId: $shipmentId, limit: 100, page: 1) {
      nodes {
        id
        referenceId
        declarationNo
        status
        declarationVerificationStatus
        workingUnit
        declarationUnit
        trackingUnit
        receivedQuantityWorking
        receivedQuantityDeclaration
        receivedQuantityTracking
        surplusQuantityWorking
        surplusQuantityDeclaration
        surplusQuantityTracking
        writeOffQuantityWorking
        writeOffQuantityDeclaration
        writeOffQuantityTracking
        adjustedQuantityWorking
        adjustedQuantityDeclaration
        adjustedQuantityTracking
        availableQuantityWorking
        availableQuantityDeclaration
        availableQuantityTracking
        ghgEmissions
        ghgEmissionSavings
        rawMaterialType
        countryOfOrigin
        feedstockCategory
        allocationPreference
        dateOfDischarge
        dateOfDispatch
        dateOfIssuance
        discrepancyCount
        discrepancies { type message }
      }
    }
  }
`;

// Edit Shipment's prefill — the form's own fields, nothing the screen doesn't edit.
export const SHIPMENT_FORM_QUERY = `
  query ShipmentForm($id: ID!) {
    shipment(id: $id) {
      id
      shipmentId
      receivedAtSite { id }
      backToBackAccount { id }
      terminalRefId
      materialId
      multiSiteTransfer
      sentBySupplier { id }
      dateOfDispatch
      dateOfDischarge
      transport
      vessel
      shipmentQuantity
      shipmentUnit
      workingUnit
      declarationUnit
      trackingUnit
      density
      densityTracking
      ghgEmissions
      ghgEmissionSavings
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
      totalContractsLinked
      totalDeclarationsLinked
      batchAttachments { file { id filename } }
    }
  }
`;

// Shipment details — Overview, meta bar, discrepancy modal, linked contracts, attachments.
export const SHIPMENT_DETAIL_QUERY = `
  query Shipment($id: ID!) {
    shipment(id: $id) {
      id
      shipmentId
      material { name }
      workingUnit
      declarationUnit
      trackingUnit
      sentBySupplier { companyname }
      receivedAtSite { siteName }
      terminal
      status
      dateOfDischarge
      dateOfDispatch
      daysLeft
      discrepancyCount
      discrepancies { type message attribute contractId declarationId daysLeft refKind }
      shipmentQuantityWorking
      shipmentQuantityDeclaration
      shipmentQuantityTracking
      receivedDeclarationQuantityWorking
      receivedDeclarationQuantityDeclaration
      receivedDeclarationQuantityTracking
      pendingShipmentQuantityWorking
      pendingShipmentQuantityDeclaration
      pendingShipmentQuantityTracking
      availableShipmentQuantityWorking
      availableShipmentQuantityDeclaration
      availableShipmentQuantityTracking
      ghgEmissions
      ghgEmissionSavings
      rawMaterialType
      countryOfOrigin
      feedstockCategory
      preferredAllocation
      assignedContractQuantityWorking
      assignedContractQuantityDeclaration
      assignedContractQuantityTracking
      unassignedContractQuantityWorking
      unassignedContractQuantityDeclaration
      unassignedContractQuantityTracking
      totalContractsLinked
      totalDeclarationsReceived
      totalDeclarationsLinked
      attachments(limit: 100, page: 1) {
        nodes { id filename attachmentType createdAt updatedAt }
        pageInfo { total }
      }
    }
  }
`;

export const incomingShipmentsEndpoints = (build) => ({
  getIncomingShipments: build.query({
    // `document` routes this through GraphQL; the list is account-scoped, so no `module`.
    query: (screenState) => ({
      document: INCOMING_SHIPMENTS_QUERY,
      variables: buildIncomingShipmentsQuery(screenState),
    }),
    transformResponse: (data) => processIncomingShipmentsPage(data.shipments),
    providesTags: (result) =>
      result
        ? [
            ...result.rows.map((row) => ({ type: TAG_TYPES.Shipment, id: row.id })),
            { type: TAG_TYPES.Shipment, id: 'LIST' },
          ]
        : [{ type: TAG_TYPES.Shipment, id: 'LIST' }],
  }),

  // Takes the page's fixed scope only — a picked filter here would hide its siblings.
  getShipmentFilterOptions: build.query({
    query: (extraFilters) => ({
      document: SHIPMENT_FILTER_OPTIONS_QUERY,
      variables: { filters: buildShipmentFiltersInput({}, extraFilters) },
    }),
    transformResponse: (data) => processShipmentFilterOptions(data.shipmentFilterOptions),
    providesTags: [{ type: TAG_TYPES.Shipment, id: 'LIST' }],
  }),

  // Multipart, not JSON — the route runs multer upload.fields, so the body is FormData.
  createIncomingShipment: build.mutation({
    query: ({ module, values }) => ({
      url: PATHS.create(module),
      method: 'POST',
      body: buildIncomingShipmentFormData(values),
    }),
    transformResponse: processCreatedIncomingShipment,
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('INCOMING_SHIPMENTS_SAVED_FLASH'),
    invalidatesTags: [{ type: TAG_TYPES.Shipment, id: 'LIST' }],
  }),

  // `locked` decides whether Terminal/Supplier disable; both come off one read.
  getShipmentForm: build.query({
    query: ({ id }) => ({ document: SHIPMENT_FORM_QUERY, variables: { id } }),
    transformResponse: (data) => ({
      values: processIncomingShipmentForm(data.shipment),
      locked: isIncomingShipmentLocked(data.shipment),
    }),
    transformErrorResponse: handledError,
    providesTags: (result, error, { id }) => [{ type: TAG_TYPES.Shipment, id }],
  }),

  // Multipart, like create — the server ignores rsaId/materialId/facility ids here.
  updateIncomingShipment: build.mutation({
    query: ({ module, id, values }) => ({
      url: PATHS.detail(module, id),
      method: 'PATCH',
      body: buildIncomingShipmentUpdateFormData(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('INCOMING_SHIPMENTS_SAVED_FLASH'),
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Shipment, id },
      { type: TAG_TYPES.Shipment, id: 'LIST' },
    ],
  }),

  // Deletes exactly ONE batch group; the server 400s if it's linked/utilized.
  deleteIncomingShipment: build.mutation({
    query: ({ module, id }) => ({
      url: PATHS.detail(module, id),
      method: 'DELETE',
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('INCOMING_SHIPMENTS_DELETED'),
    // Drop the deleted row's own tag plus the list so the table refetches once.
    invalidatesTags: (result, error, { id }) => [
      { type: TAG_TYPES.Shipment, id },
      { type: TAG_TYPES.Shipment, id: 'LIST' },
    ],
  }),

  // Shipment Details page — Overview tab, meta bar, discrepancy modal, Attachments tab.
  getShipmentDetail: build.query({
    query: (id) => ({ document: SHIPMENT_DETAIL_QUERY, variables: { id } }),
    transformResponse: (data) => processIncomingShipmentDetail(data.shipment),
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Shipment, id }],
  }),

  // Linked Contracts tab; the unlink mutation stays REST — the schema has no mutations.
  getLinkedContractsForShipment: build.query({
    query: ({ id, filters }) => ({
      document: LINKED_CONTRACTS_FOR_SHIPMENT_QUERY,
      variables: { shipmentId: id, filters: buildContractFiltersInput(filters) },
    }),
    transformResponse: (data) => processLinkedContracts(data.linkedContractsForShipment),
    providesTags: (result, error, arg) => [linkedContractsTag(arg.id)],
  }),

  getLinkedDeclarationsForShipment: build.query({
    query: ({ id }) => ({
      document: LINKED_DECLARATIONS_FOR_SHIPMENT_QUERY,
      variables: { shipmentId: id },
    }),
    transformResponse: (data) => processLinkedDeclarations(data.linkedDeclarationsForShipment),
    providesTags: (result, error, arg) => [{ type: TAG_TYPES.Shipment, id: arg.id }],
  }),

  unlinkContractFromShipment: build.mutation({
    query: ({ module, id, contractId }) => ({
      url: PATHS.unlinkContract(module, id, contractId),
      method: 'DELETE',
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('INCOMING_SHIPMENTS_LINKED_CONTRACTS_REMOVED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Shipment, id: arg.id },
      linkedContractsTag(arg.id),
    ],
  }),
});
